import * as React from 'react';
import styled from '@emotion/styled';
import { border, transparentize, tint } from 'polished';

import { useComponentTheme } from '../useComponentTheme';

type Props = {
  children: Node,
  className?: string,
  onClick?: Function,
  isSelected?: boolean,
  type?: 'line' | 'card',
  schemeMode?: 'dark' | 'light',
  mode?: 'Meal' | 'MealDealCategories',
  elemRef?: Function,
  handleClick?: Function,
  active?: Boolean,
  isLastTab?: Boolean,
  lastElSticky?: Boolean,
  disabled?: Boolean
};

export const Tab = ({
  children,
  className,
  isSelected,
  onClick,
  mode,
  schemeMode,
  type,
  elemRef,
  handleClick,
  active,
  isLastTab,
  lastElSticky,
  disabled,
  ...rest
}: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Tabs, mode);

  const onTabClick = () => {
    onClick();
    handleClick && handleClick();
  };

  return (
    <StyledTab
      className={className}
      role="tab"
      isSelected={isSelected}
      onClick={onTabClick}
      type={type}
      schemeMode={schemeMode}
      componentTheme={componentTheme}
      ref={isSelected ? elemRef: null}
      lastElSticky={lastElSticky}
      disabled={disabled}
      {...rest}
    >
      {children}
    </StyledTab>
  );
};

Tab.tabsRole = 'Tab';

const greyMode = (theme, schemeMode) => (schemeMode === 'dark' ? theme.color.grey : theme.color.lightGrey);

const StyledTab = styled('li')`
  flex: 1 0 0;
  max-width: 100%;
  list-style: none;
  font-size: ${({ componentTheme }) => componentTheme.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  text-align: center;
  padding: ${({ type }) => (type !== 'card' ? '10px' : '10px 0')};
  cursor: pointer;
  border-radius: ${({ componentTheme }) => componentTheme.borderRadius};
  transition: ${({ theme }) => theme.motion.transition};
  position: relative;
  ${({ isSelected, theme, schemeMode, componentTheme, type }) =>
    type === 'card'
      ? {
          ...border(
            'top',
            5,
            'solid',
            isSelected ? componentTheme.activeBorderColor || theme.color.brand : 'transparent'
          ),
          ...border('left', isSelected ? 1 : 0, 'solid', greyMode(theme, schemeMode)),
          ...border('right', isSelected ? 1 : 0, 'solid', greyMode(theme, schemeMode)),
          ...border('bottom', 1, 'solid', isSelected ? 'transparent' : greyMode(theme, schemeMode)),
          color: schemeMode === 'dark' ? theme.color.white : theme.color.black,
        }
      : isSelected
      ? {
          borderBottom: `5px solid ${componentTheme.activeBorderColor || theme.color.brand}`,
          border: componentTheme.activeBorder,
          color: componentTheme.activeColor || theme.color.brand,
          backgroundColor: componentTheme.activeBackgroundColor || transparentize(0.9, theme.color.brand),
        }
      : {
          borderBottom: '5px solid transparent',
          border: componentTheme.border || componentTheme.activeBorder,
          borderColor: componentTheme.activeBorder && 'transparent',
          color: componentTheme.color,
          backgroundColor: componentTheme.backgroundColor,
        }};

  &:hover {
    color: ${({ isSelected, theme }) => !isSelected && theme.color.brand};
  }

  ${({ disabled, theme }) =>
  disabled &&
  `
  &:first-of-type {
    background-color: ${tint(0.6, theme.color.lightGrey)};
    border-color: ${tint(0.6, theme.color.lightGrey)};
    color: ${theme.color.black};
    pointer-events:none;
  };
`}

  ${({ type, lastElSticky }) =>
    type !== 'card' && lastElSticky &&
    `
    &:last-child {
      position: sticky;
      z-index: 1;
      right: 0;
    };
`}

  @media (${({ theme }) => theme.breakpoint.kiosk}) {
    padding: ${({ type }) => (type !== 'card' ? '24px 10px' : '16px 0')};
  }
`;
